import _ from "lodash";
import type { VatAddress } from "../types/vatAddress";
import * as vatAddress from "../types/vatAddress";

import { isNYAdministrativeDivisionCode } from "./administrativeDivisionService";

enum CountryWithVat {
  GB = 74,
  DE = 55,
  US = 221,
}

enum CollectInStudio {
  NotSet = -1,
  GB = 1,
  DE = 13,
}

// ----------------------------------------------------------------------------

enum Branch {
  NotSet = -1,
  GB = 1,
  DE = 2,
  US = 3,
}

// ----------------------------------------------------------------------------

enum OrderType {
  InHouseOrder,
  WebOrder,
  EcommerceOrder,
}

// ----------------------------------------------------------------------------

const GbBranchAddress = () => ({
  Id: 0,
  CountryCode: "GB",
  CountryId: CountryWithVat.GB,
  County: "",
  PostCode: "E2 8DL",
  Town: "London",
  IsEu: false,
  IsUkForVAT: true,
});

// ----------------------------------------------------------------------------

const DeBranchAddress = () => ({
  Id: 0,
  CountryCode: "DE",
  CountryId: CountryWithVat.DE,
  County: "",
  PostCode: "",
  Town: "Düsseldorf",
  IsEu: true,
  IsUkForVAT: false,
});

// ----------------------------------------------------------------------------

type OrderDTO = {
  OrderTypeId: number;
  ShippingAddresses: VatAddress[];
  ShippingAddressId: number;
  ProductionBranchId: Branch;
  DeliveryOptionId: number;
  ShippingCountryIsEU: boolean;
  ShippingCountryIsUK: boolean;
  Vat: number;
  VatNumber: string | null | undefined;
};

// ----------------------------------------------------------------------------

class VatOrderWrap {
  private readonly _orderType: OrderType;
  private readonly _shippingAddresses: VatAddress[];
  private readonly _shippingAddressId: number;
  private readonly _deliveryBranchId: Branch;
  private readonly _deliveryOptionId: number;
  private readonly _shippingCountryIsEU: boolean;
  private readonly _shippingCountryIsUK: boolean;
  private readonly _vat: number;
  private readonly _vatNumber: string | null | undefined;

  constructor(order: OrderDTO) {
    this._orderType =
      order.OrderTypeId == 1
        ? OrderType.WebOrder
        : order.OrderTypeId == 11
        ? OrderType.EcommerceOrder
        : OrderType.InHouseOrder;
    this._shippingAddressId = order.ShippingAddressId;

    this._shippingAddresses = [
      ..._.cloneDeep(order.ShippingAddresses),
      this.collectInStudioAddress(order),
    ];
    this._deliveryBranchId = order.ProductionBranchId;
    this._deliveryOptionId = order.DeliveryOptionId;
    this._shippingCountryIsEU = order.ShippingCountryIsEU;
    this._shippingCountryIsUK = order.ShippingCountryIsUK;
    this._vat = order.Vat;
    this._vatNumber = order.VatNumber;
  }

  // ----------------------------------------------------------------------------

  get orderType(): number {
    return this._orderType;
  }

  // ----------------------------------------------------------------------------

  get shippingAddresses(): VatAddress[] {
    return this._shippingAddresses;
  }

  // ----------------------------------------------------------------------------

  get shippingAddressId(): number {
    return this._shippingAddressId;
  }

  // ----------------------------------------------------------------------------

  get deliveryBranchId(): Branch {
    return this._deliveryBranchId;
  }

  // ----------------------------------------------------------------------------

  get deliveryOptionId(): number {
    return this._deliveryOptionId;
  }

  // ----------------------------------------------------------------------------

  get shippingCountryIsEU(): boolean {
    return this._shippingCountryIsEU;
  }

  // ----------------------------------------------------------------------------

  get shippingCountryIsUK(): boolean {
    return this._shippingCountryIsUK;
  }

  // ----------------------------------------------------------------------------

  get vat(): number {
    return this._vat;
  }

  // ----------------------------------------------------------------------------

  get vatNumber(): string | null | undefined {
    return this._vatNumber;
  }

  // ----------------------------------------------------------------------------

  clone(): VatOrderWrap {
    return _.cloneDeep(this);
  }

  //---------------------------------------------------------------------------

  private isCollectInStudio(order: OrderDTO): boolean {
    return [CollectInStudio.GB, CollectInStudio.DE].includes(
      order.DeliveryOptionId ?? CollectInStudio.NotSet,
    );
  }

  //---------------------------------------------------------------------------

  private collectInStudioAddress(order: OrderDTO): VatAddress {
    if (order.DeliveryOptionId == CollectInStudio.GB) {
      return GbBranchAddress();
    }
    if (order.DeliveryOptionId == CollectInStudio.DE) {
      return DeBranchAddress();
    }
    return vatAddress.empty();
  }
}

// ----------------------------------------------------------------------------

export enum VATStatus {
  OK = 0,
  RequiresRefund,
  RequiresRecharge,
}

// ----------------------------------------------------------------------------

export type VatStatusResult = Readonly<{
  vatStatusChanged: boolean;
  vatStatus: VATStatus;
}>;

// ----------------------------------------------------------------------------
const Ok = () =>
  ({
    vatStatusChanged: false,
    vatStatus: VATStatus.OK,
  } as VatStatusResult);

// ----------------------------------------------------------------------------

const RequiresRefund = () =>
  ({
    vatStatusChanged: true,
    vatStatus: VATStatus.RequiresRefund,
  } as VatStatusResult);

// ----------------------------------------------------------------------------

const RequiresRecharge = () =>
  ({
    vatStatusChanged: true,
    vatStatus: VATStatus.RequiresRecharge,
  } as VatStatusResult);

// ----------------------------------------------------------------------------

const CountriesWithVats = (
  previousCountry: CountryWithVat,
  currentCountry: CountryWithVat,
) => {
  // Always requires refund as GB has the highest VAT
  if (previousCountry == CountryWithVat.GB) {
    return RequiresRefund();
  }
  // Always requires recharge as US has the lowest VAT
  if (previousCountry == CountryWithVat.US) {
    return RequiresRecharge();
  }
  // This is the case when previousCountry is Germany
  if (currentCountry == CountryWithVat.GB) {
    return RequiresRecharge();
  }
  if (currentCountry == CountryWithVat.US) {
    return RequiresRefund();
  }

  return RequiresRecharge();
};

// ----------------------------------------------------------------------------

export interface IVatService {
  vatStatus(order: OrderDTO): VatStatusResult;
}

// ----------------------------------------------------------------------------
const isDeVatNumber = (vatNumber: string | null | undefined) => {
  if (!vatNumber) return false;

  const euVatPrefixes = ["DE"];

  const vatPrefix = vatNumber.substring(0, 2);

  return euVatPrefixes.includes(vatPrefix);
};
// ----------------------------------------------------------------------------

const isEuVatNumber = (vatNumber: string | null | undefined) => {
  if (!vatNumber) return false;

  const euVatPrefixes = [
    "AT",
    "BE",
    "BG",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "EL",
    "ES",
    "FI",
    "FR",
    "HU",
    "IE",
    "IT",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
  ];

  const vatPrefix = vatNumber.substring(0, 2);

  return euVatPrefixes.includes(vatPrefix);
};

// ----------------------------------------------------------------------------

const isUkVatNumber = (vatNumber: string | null | undefined) => {
  if (!vatNumber) return false;

  return vatNumber.startsWith("GB");
};

// ----------------------------------------------------------------------------

const isShippingCountrySameAsDeliveryBranchCountry = (
  shippingAddress: VatAddress,
  deliveryBranchId: Branch,
): boolean => {
  if (deliveryBranchId == Branch.GB) {
    return shippingAddress.IsUkForVAT;
  }
  if (deliveryBranchId == Branch.DE) {
    return shippingAddress.CountryCode == "DE";
  }

  if (deliveryBranchId == Branch.US) {
    return shippingAddress.CountryCode == "US";
  }

  return false;
};

// ----------------------------------------------------------------------------

const isClientVatRegistered = (
  shippingAddress: VatAddress,
  deliveryBranchId: Branch,
  vatNumber: string | null | undefined,
) => {
  const rules = {
    [Branch.GB]: () => shippingAddress.IsUkForVAT && isUkVatNumber(vatNumber),
    [Branch.DE]: () =>
      shippingAddress.CountryCode == "DE" && isEuVatNumber(vatNumber),
    [Branch.US]: () => false,
    [Branch.NotSet]: () => false,
  };

  return !!vatNumber && rules[deliveryBranchId]();
};

// ----------------------------------------------------------------------------

export default class VatService implements IVatService {
  private readonly _originalOrder: VatOrderWrap;
  private _order: VatOrderWrap | null = null;

  constructor(order: OrderDTO) {
    const vatOrder = new VatOrderWrap(order);
    this._originalOrder = vatOrder.clone();
    console.info("VatService :: Original Order", this._originalOrder);
  }

  //---------------------------------------------------------------------------

  private currentAddress(addressId: number): VatAddress {
    return (
      this._order?.shippingAddresses.find(o => o.Id == addressId) ??
      vatAddress.empty()
    );
  }

  //---------------------------------------------------------------------------

  private previousAddress(addressId: number): VatAddress {
    return (
      this._originalOrder.shippingAddresses.find(o => o.Id == addressId) ??
      vatAddress.empty()
    );
  }

  //---------------------------------------------------------------------------
  private sameCountry(
    previousAddress: VatAddress,
    currentAddress: VatAddress,
  ): boolean {
    return previousAddress.CountryId == currentAddress.CountryId;
  }

  //---------------------------------------------------------------------------

  private sameTaxAreaById(
    previousAddressId: number,
    currentAddressId: number,
  ): boolean {
    const previousAddress = this.previousAddress(previousAddressId);
    const currentAddress = this.currentAddress(currentAddressId);

    if (!this.sameCountry(previousAddress, currentAddress)) {
      return false;
    }

    if (currentAddress.CountryId === CountryWithVat.US) {
      const isCurrentAddressNY = isNYAdministrativeDivisionCode(currentAddress);
      const isPreviousAddressNY =
        isNYAdministrativeDivisionCode(previousAddress);

      const bothNY = isCurrentAddressNY && isPreviousAddressNY;
      const bothNotNY = !isCurrentAddressNY && !isPreviousAddressNY;

      return bothNY || bothNotNY;
    }

    return this.sameCountry(previousAddress, currentAddress);
  }

  //---------------------------------------------------------------------------

  private vatCollected(): boolean {
    return (this._order?.vat ?? 0) > 0;
  }

  //---------------------------------------------------------------------------

  private deliveryBranchChanged(): boolean {
    return (
      this._order?.deliveryBranchId != this._originalOrder.deliveryBranchId
    );
  }

  //---------------------------------------------------------------------------

  private isVatRequired() {
    const currentAddress = this.currentAddress(
      this._order?.shippingAddressId ?? 0,
    );
    const deliveryBranchId = this._order?.deliveryBranchId ?? Branch.NotSet;
    const rules = {
      [Branch.GB]: () =>
        isShippingCountrySameAsDeliveryBranchCountry(currentAddress, Branch.GB),
      [Branch.DE]: () =>
        isShippingCountrySameAsDeliveryBranchCountry(
          currentAddress,
          Branch.DE,
        ) ||
        isClientVatRegistered(
          currentAddress,
          Branch.DE,
          this._order?.vatNumber,
        ),
      [Branch.US]: () =>
        this._order?.orderType == OrderType.WebOrder &&
        isNYAdministrativeDivisionCode(currentAddress),
      [Branch.NotSet]: () => false,
    };

    return rules[deliveryBranchId]();
  }

  //---------------------------------------------------------------------------
  private countriesWithVats() {
    console.info(
      "VatService :: countriesWithVats :: this._originalOrder",
      this._originalOrder,
    );
    console.info("VatService :: countriesWithVats :: this._order", this._order);

    const previousAddressId = this._originalOrder.shippingAddressId;
    const currentAddressId = this._order?.shippingAddressId ?? 0;

    const previousAddress = this.previousAddress(previousAddressId);
    const currentAddress = this.currentAddress(currentAddressId);

    return CountriesWithVats(
      previousAddress.CountryId,
      currentAddress.CountryId,
    );
  }
  //---------------------------------------------------------------------------

  vatStatus(order: OrderDTO): VatStatusResult {
    this._order = new VatOrderWrap(order);

    console.info(
      "VatService :: vatStatus :: this._originalOrder",
      this._originalOrder,
    );
    console.info("VatService :: vatStatus :: this._order", this._order);

    const [deliveryBranchChanged, vatRequired, vatCollected] = [
      this.deliveryBranchChanged(), // for debugging purposes
      this.isVatRequired(),
      this.vatCollected(),
    ];

    if (
      this.sameTaxAreaById(
        this._originalOrder.shippingAddressId,
        this._order?.shippingAddressId ?? 0,
      ) &&
      !deliveryBranchChanged
    ) {
      return Ok();
    }

    const cases = [
      {
        name: "CountriesWithVats",
        condition: vatRequired && vatCollected,
        result: () => this.countriesWithVats(),
      },
      {
        name: "RequiresRecharge",
        condition: vatRequired && !vatCollected,
        result: RequiresRecharge,
      },
      {
        name: "RequiresRefund",
        condition: !vatRequired && vatCollected,
        result: RequiresRefund,
      },
    ];

    let ct = 0;
    for (const { name, condition, result } of cases) {
      if (condition) {
        const res = result();
        return res;
      }
      ct++;
    }

    return Ok();
  }

  //---------------------------------------------------------------------------
}
